import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShoppingBag, Loader2, ShieldCheck, Clock, ChevronLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const COMMISSION_RATE = 0.05; // mirror backend default for display (server is authoritative)

function formatPrice(amount, currency, locale = "en") {
    try {
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: (currency || "EUR").toUpperCase(),
            maximumFractionDigits: 2,
        }).format(amount);
    } catch {
        return `${amount} ${currency}`;
    }
}

export default function Listing() {
    const { listingId } = useParams();
    const { t, i18n } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [listing, setListing] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [accepted, setAccepted] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(`/marketplace/listings/${listingId}`);
                if (alive) setListing(data);
            } catch (e) {
                if (alive) setError(formatApiError(e.response?.data?.detail));
            }
        })();
        return () => { alive = false; };
    }, [listingId]);

    const handleBuy = async () => {
        if (!user || typeof user !== "object") {
            toast.error(t("marketplace.login_to_buy"));
            navigate("/acceder");
            return;
        }
        if (!accepted) {
            toast.error(t("marketplace.must_accept_terms"));
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/marketplace/checkout", {
                listing_id: listing.id,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
            setBusy(false);
        }
    };

    if (error) {
        return (
            <main className="min-h-[calc(100vh-73px)] flex flex-col items-center justify-center px-6 text-center">
                <p className="text-[var(--ink-muted)] mb-4">{error}</p>
                <Link to="/marketplace" className="border border-[var(--ink)] text-[var(--ink)] rounded-full hover:bg-[var(--bg-hover)] transition-colors px-6 py-2 text-sm uppercase tracking-widest">
                    {t("marketplace.go_marketplace")}
                </Link>
            </main>
        );
    }
    if (!listing) {
        return <main className="min-h-[calc(100vh-73px)] max-w-5xl mx-auto px-6 py-24"><p className="font-display text-2xl italic text-[var(--ink)] animate-slow-pulse">…</p></main>;
    }

    const commission = Math.round(listing.price * COMMISSION_RATE * 100) / 100;
    const sellerNet = Math.round((listing.price - commission) * 100) / 100;
    const isOwn = user && typeof user === "object" && user.id === listing.seller_id;

    return (
        <main className="min-h-[calc(100vh-73px)] max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-20">
            <Link to="/marketplace" className="text-xs uppercase tracking-[0.3em] text-[var(--ink-muted)] hover:text-[var(--accent)] transition-colors mb-6 inline-flex items-center gap-1">
                <ChevronLeft size={14} strokeWidth={1.8} /> {t("marketplace.title")}
            </Link>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                {/* Cover */}
                <div className="aspect-[2/3] bg-[var(--bg-strong)] border border-[var(--border-strong)] overflow-hidden flex items-center justify-center max-w-sm">
                    {listing.cover_url ? (
                        <img src={listing.cover_url} alt={listing.title} className="w-full h-full object-cover" />
                    ) : (
                        <ShoppingBag size={32} strokeWidth={1} className="text-[var(--accent)]" />
                    )}
                </div>

                {/* Details */}
                <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--accent)] mb-3">
                        {t(`marketplace.condition_${listing.condition}`)}
                    </p>
                    <h1
                        className="font-display text-4xl md:text-5xl text-[var(--ink)] leading-tight mb-3"
                        style={{ fontVariationSettings: '"wght" 700, "wdth" 80, "opsz" 96' }}
                        data-testid="listing-title"
                    >
                        {listing.title}
                    </h1>
                    <p className="text-base text-[var(--ink-muted)] mb-6">{listing.author}</p>
                    {listing.description && (
                        <p className="text-sm text-[var(--ink-muted)] leading-relaxed mb-8">{listing.description}</p>
                    )}
                    {listing.seller_name && (
                        <p className="text-xs text-[var(--ink-dim)] mb-8">
                            {t("marketplace.by_seller", { seller: listing.seller_name })}
                        </p>
                    )}

                    {/* Price breakdown */}
                    <section className="border border-[var(--border-strong)] bg-[var(--bg-elevated)] p-5 mb-6" data-testid="price-breakdown">
                        <p className="text-xs uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-4">{t("marketplace.cost_breakdown")}</p>
                        <Row label={t("marketplace.cost_book_price")} value={formatPrice(listing.price, listing.currency, i18n.language)} />
                        <Row label={t("marketplace.cost_seller_receives")} value={formatPrice(sellerNet, listing.currency, i18n.language)} muted />
                        <Row label={t("marketplace.cost_commission")} value={formatPrice(commission, listing.currency, i18n.language)} muted />
                        <div className="border-t border-[var(--border)] mt-3 pt-3 flex items-baseline justify-between">
                            <span className="text-sm uppercase tracking-widest text-[var(--ink)]">{t("marketplace.cost_total")}</span>
                            <span className="font-display text-2xl text-[var(--ink)] font-mono-num"
                                  style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}>
                                {formatPrice(listing.price, listing.currency, i18n.language)}
                            </span>
                        </div>
                    </section>

                    {/* Escrow notice */}
                    <section className="flex gap-3 mb-6" data-testid="escrow-notice">
                        <ShieldCheck size={20} strokeWidth={1.5} className="text-[var(--accent)] flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm text-[var(--ink)] leading-snug mb-1">{t("marketplace.escrow_title")}</p>
                            <p className="text-xs text-[var(--ink-dim)] leading-relaxed">{t("marketplace.escrow_lead")}</p>
                        </div>
                    </section>

                    {/* Terms checkbox */}
                    {!isOwn && listing.status === "available" && (
                        <label className="flex items-start gap-3 mb-6 cursor-pointer group">
                            <input
                                type="checkbox"
                                data-testid="accept-terms"
                                checked={accepted}
                                onChange={(e) => setAccepted(e.target.checked)}
                                className="mt-1 sr-only"
                            />
                            <span className={`mt-0.5 w-4 h-4 border flex items-center justify-center flex-shrink-0 transition-colors ${
                                accepted
                                    ? "bg-[var(--accent)] border-[var(--accent)] text-[var(--bg)]"
                                    : "bg-transparent border-[var(--border-strong)] group-hover:border-[var(--ink)]"
                            }`}>
                                {accepted && <span className="text-[10px]">✓</span>}
                            </span>
                            <span className="text-xs text-[var(--ink-muted)] leading-relaxed">
                                {t("marketplace.accept_terms_pre")}{" "}
                                <Link to="/terminos" target="_blank" className="text-[var(--accent)] hover:underline">
                                    {t("marketplace.accept_terms_link")}
                                </Link>{" "}
                                {t("marketplace.accept_terms_post")}
                            </span>
                        </label>
                    )}

                    {/* CTA */}
                    {isOwn ? (
                        <p className="text-xs text-[var(--ink-dim)] italic">{t("marketplace.is_own_listing")}</p>
                    ) : listing.status !== "available" ? (
                        <p className="text-xs text-[var(--ink-dim)] italic">
                            {t(`marketplace.status_${listing.status}`)}
                        </p>
                    ) : (
                        <button
                            data-testid="checkout-buy-btn"
                            onClick={handleBuy}
                            disabled={busy || !accepted}
                            className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors px-8 py-3 text-sm uppercase tracking-widest"
                        >
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <ShoppingBag size={14} strokeWidth={1.5} />}
                            <span>{t("marketplace.proceed_to_payment")}</span>
                        </button>
                    )}
                    <p className="text-xs text-[var(--ink-dim)] mt-3 inline-flex items-center gap-1.5">
                        <Clock size={12} strokeWidth={1.5} /> {t("marketplace.escrow_window_note")}
                    </p>
                </div>
            </div>
        </main>
    );
}

function Row({ label, value, muted }) {
    return (
        <div className="flex items-baseline justify-between gap-3 py-1.5">
            <span className={`text-sm ${muted ? "text-[var(--ink-muted)]" : "text-[var(--ink)]"}`}>{label}</span>
            <span className={`font-mono-num text-sm ${muted ? "text-[var(--ink-muted)]" : "text-[var(--ink)]"}`}>{value}</span>
        </div>
    );
}
